import type { LegacyRef, MouseEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Dimensions } from '@floating-ui/core';
import { computePosition, shift } from '@floating-ui/core';
import { platform as platformDom } from '@floating-ui/dom';
import mergeRefs from 'merge-refs';
import { identity } from 'lodash';
import type { RefRegistry } from '../ref-registry';
import SelectionBlock from '../selectionBlock/SelectionBlock';
import { useIsOverflow } from '../hooks/useIsOverflow';
import type { TimeWindow } from '../utils';

interface BlockPeriod {
    timeWindow: TimeWindow;
    value?: number;
}

interface Block {
    period: BlockPeriod;
    width: number;
    left: number;
}

export interface SelectionWrapperProps {
    block: Block;
    unit?: string;
    id: string;
    buildMode?: boolean;
    openEditModal?: (timeWindow?: TimeWindow) => void;
    isActive?: boolean;
    refRegistry: RefRegistry;
}

function SelectionWrapper({
    block,
    unit,
    id,
    buildMode,
    openEditModal,
    isActive,
    refRegistry,
}: SelectionWrapperProps) {
    const { period, width, left } = block;
    const { timeWindow, value } = period;
    const elementRef = useRef<HTMLDivElement>(null);
    const [isHovered, setIsHovered] = useState(false);
    const [overriddenLeft, setOverriddenLeft] = useState<number | null>(null);

    const overflowContainerRef = useRef<HTMLDivElement>(null);
    const isCompact = useIsOverflow(overflowContainerRef, isHovered);

    useEffect(() => {
        if (!(isHovered && isCompact)) {
            setOverriddenLeft(null);
            return;
        }

        const element = elementRef.current!;
        const parent = element.parentElement!;

        const canvas = {
            x: 0,
            y: 0,
            ...(platformDom.getDimensions(parent) as Dimensions),
        };

        // The `reference` contains initial width and left
        // position of the block.
        const reference = {
            width,
            height: 0,
            x: left,
            y: 0,
        };

        // `floating` is the Selection Block.
        const floating = {
            x: 0,
            y: 0,
            ...(platformDom.getDimensions(element) as Dimensions),
        };

        void computePosition(reference, floating, {
            platform: {
                getDimensions: platformDom.getDimensions,
                getElementRects: identity,
                getClippingRect() {
                    return canvas;
                },
            },
            middleware: [shift()],
        }).then(({ x }) => {
            setOverriddenLeft(x);
        });
    }, [isHovered, block, isCompact, width, left]);

    const handleBlockClick = useCallback(
        (event: MouseEvent<HTMLDivElement>) => {
            if (!buildMode) return;
            event.stopPropagation();

            openEditModal?.(timeWindow.sourceTimeWindow);
        },
        [buildMode, openEditModal, timeWindow]
    );

    return (
        <div
            className="absolute hover:z-10"
            style={{
                left: overriddenLeft || block.left,
                width: isCompact && isHovered ? 270 : block.width,
            }}
            onMouseOver={() => !buildMode && setIsHovered(true)}
            onMouseLeave={() => !buildMode && setIsHovered(false)}
            data-testid={id}
            ref={
                mergeRefs(
                    elementRef,
                    refRegistry.setTimeWindow(timeWindow)
                ) as LegacyRef<HTMLDivElement>
            }
        >
            <div
                onClick={handleBlockClick}
                onMouseDown={(e) => e.stopPropagation()}
                className={buildMode ? 'cursor-pointer' : ''}
                data-testid={`block-${id}`}
            >
                <SelectionBlock
                    ref={overflowContainerRef}
                    {...{
                        value,
                        timeWindow,
                        isCompact: isCompact && !isHovered,
                        unit,
                        isActive,
                    }}
                />
            </div>
        </div>
    );
}

export default SelectionWrapper;
